// How close to the viewport a slider has to get before its logos are loaded and the
// marquee is built. Sliders further down the page cost nothing until then.
const VIEWPORT_MARGIN = '400px';

// Upper bound for waiting on the logo images. A stalled request must not keep the
// marquee stopped; the resize observer corrects the layout once the image arrives.
const IMAGE_WAIT_LIMIT = 3000;

// From this many logos on, the row is busy enough that it should scroll on its own,
// even when the logos still fit and "always animate" is off.
const ANIMATE_FROM_SLIDES = 5;

// How long the sizes have to stay put before the marquee is recalculated. The resize
// observer already reports at most once per frame; this collapses a whole window drag
// into a single pass.
const RESIZE_DEBOUNCE = 150;

// Width the configured animation speed is calibrated against. A row this wide takes
// exactly the configured number of seconds per lap; narrower/wider rows scale the
// duration so the on-screen speed (px/s) stays the same regardless of how many logos
// are in the block.
const SPEED_REFERENCE_WIDTH = 1440;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const PLAY_CLASS = 'b-logo-slider__marquee--play';
const PAUSED_CLASS = 'b-logo-slider--paused';

// Maps every observed element (block wrapper, clipping element, original logo set) to
// its slider, so all instances on the page share one observer of each kind.
const instances = new WeakMap();

let visibilityObserver = null;
let sizeObserver = null;

/**
 * Resolve once every logo image has finished loading (or failed), or once the wait
 * limit runs out, whichever comes first.
 *
 * @param {HTMLElement} element Element holding the logo images.
 * @return {Promise<void>} Resolves once the images have settled.
 */
const waitForImages = ( element ) => {

	const images = Array.from( element.querySelectorAll( 'img' ) );

	const settled = Promise.all( images.map( ( img ) => ( img.complete
		? Promise.resolve()
		: new Promise( ( resolve ) => {

			img.addEventListener( 'load', resolve, { once: true } );
			img.addEventListener( 'error', resolve, { once: true } );

		} )
	) ) );

	const limit = new Promise( ( resolve ) => window.setTimeout( resolve, IMAGE_WAIT_LIMIT ) );

	return Promise.race( [ settled, limit ] );

};

/**
 * Shared observer that starts sliders as they approach the viewport and pauses the
 * animation of the ones that have scrolled away.
 *
 * @return {IntersectionObserver} Observer instance.
 */
const getVisibilityObserver = () => {

	if ( ! visibilityObserver ) {

		visibilityObserver = new window.IntersectionObserver( ( entries ) => {

			entries.forEach( ( entry ) => {

				instances.get( entry.target )?.handleVisibility( entry.isIntersecting );

			} );

		}, { rootMargin: VIEWPORT_MARGIN } );

	}

	return visibilityObserver;

};

/**
 * Shared observer for the clipping element and the original logo set of every slider.
 * The sizes come straight from the entries, so the callback never forces a layout.
 *
 * @return {ResizeObserver} Observer instance.
 */
const getSizeObserver = () => {

	if ( ! sizeObserver ) {

		sizeObserver = new window.ResizeObserver( ( entries ) => {

			const changed = new Set();

			entries.forEach( ( entry ) => {

				const slider = instances.get( entry.target );

				if ( slider && slider.storeSize( entry.target, entry.contentRect.width ) ) {

					changed.add( slider );

				}

			} );

			changed.forEach( ( slider ) => slider.scheduleUpdate() );

		} );

	}

	return sizeObserver;

};

/**
 * Continuous logo marquee driven by a CSS keyframe animation on the marquee row.
 *
 * The row holds two identical halves - the logos from the markup plus clones - and the
 * animation moves it by exactly -50%, so the loop is seamless at any width without the
 * script having to recalculate the distance. Below the configured breakpoint the
 * slider is always used, so a grid never has to squeeze onto a phone.
 */
class LogoSlider {

	/**
	 * Wire up a single block instance.
	 *
	 * @param {HTMLElement} element Block wrapper element.
	 */
	constructor( element ) {

		this.element = element;
		this.scrollEl = element.querySelector( '.js-logo-slider-scroll' );
		this.row = this.scrollEl?.querySelector( '.js-logo-slider-marquee' );
		this.item = this.scrollEl?.querySelector( '.js-logo-slider-marquee-item' );
		this.breakpoint = parseInt( element.dataset.breakpoint, 10 ) || 768;
		this.animationSpeed = parseFloat( element.dataset.animationSpeed ) || 20;
		this.breakpointQuery = window.matchMedia( `(max-width: ${ this.breakpoint - 0.02 }px)` );
		this.prefersReducedMotion = window.matchMedia( REDUCED_MOTION_QUERY ).matches;
		this.alwaysAnimate = element.classList.contains( 'b-logo-slider--always-animate' );
		this.isCrowded = this.item
			? this.item.querySelectorAll( '.b-logo-slider__slide' ).length >= ANIMATE_FROM_SLIDES
			: false;

		this.isStarted = false;
		this.hasUpdated = false;
		this.updateTimeout = null;
		this.containerWidth = 0;
		this.itemWidth = 0;
		this.gap = 0;
		this.copies = 0;
		this.duration = '';

		// The grid/slider class only depends on the viewport, not on the logo images, so
		// it can be applied right away instead of waiting for the marquee build.
		this.syncResponsiveMode();

		this.breakpointQuery.addEventListener( 'change', () => {

			this.handleModeChange();

		} );

		if ( this.row && this.item ) {

			instances.set( element, this );
			getVisibilityObserver().observe( element );

			// Focusing a logo link makes the browser scroll the clipping element to reveal
			// it, even with overflow hidden. Undo that once focus leaves the row, otherwise
			// the shift stays and the loop shows a gap at its end.
			this.scrollEl.addEventListener( 'focusout', ( event ) => {

				if ( ! this.scrollEl.contains( event.relatedTarget ) ) {

					this.scrollEl.scrollLeft = 0;

				}

			} );

		}

	}

	/**
	 * Initialise every logo slider on the page.
	 *
	 * @return {void}
	 */
	static init() {

		document.querySelectorAll( '.b-logo-slider' ).forEach( ( slider ) => new LogoSlider( slider ) );

	}

	/**
	 * React to the slider entering or leaving the viewport (plus margin). The first
	 * time it comes close, the logos are loaded and the marquee gets built; after that
	 * this only pauses and resumes the animation.
	 *
	 * @param {boolean} isVisible Whether the slider is within the viewport margin.
	 * @return {void}
	 */
	handleVisibility( isVisible ) {

		this.element.classList.toggle( PAUSED_CLASS, ! isVisible );

		if ( ! isVisible || this.isStarted ) {

			return;

		}

		this.isStarted = true;

		// Logos clipped by the overflow may never count as "in view" for native lazy
		// loading. The slider is about to be seen anyway, so load them all now.
		this.item.querySelectorAll( 'img' ).forEach( ( img ) => {

			img.loading = 'eager';

		} );

		waitForImages( this.item ).then( () => this.start() );

	}

	/**
	 * Start watching the sizes. The observer reports both of them right away, which
	 * triggers the first build.
	 *
	 * @return {void}
	 */
	start() {

		// The gap between logos is also the gap between two copies of the set. If the
		// browser does not report it, the halves come out slightly wider than needed,
		// which is harmless.
		this.gap = parseFloat( window.getComputedStyle( this.item ).columnGap ) || 0;

		instances.set( this.scrollEl, this );
		instances.set( this.item, this );

		getSizeObserver().observe( this.scrollEl );
		getSizeObserver().observe( this.item );

	}

	/**
	 * Remember a size reported by the resize observer.
	 *
	 * @param {Element} target Observed element.
	 * @param {number} width Its content box width.
	 * @return {boolean} True when the width actually changed.
	 */
	storeSize( target, width ) {

		const key = target === this.scrollEl ? 'containerWidth' : 'itemWidth';

		if ( this[ key ] === width ) {

			return false;

		}

		this[ key ] = width;

		return true;

	}

	/**
	 * Recalculate the marquee once the sizes have settled. The very first pass runs
	 * immediately, so the slider does not start late on page load.
	 *
	 * @return {void}
	 */
	scheduleUpdate() {

		if ( ! this.hasUpdated ) {

			this.hasUpdated = true;
			this.update();

			return;

		}

		window.clearTimeout( this.updateTimeout );
		this.updateTimeout = window.setTimeout( () => this.update(), RESIZE_DEBOUNCE );

	}

	/**
	 * Toggle the grid/slider classes after the breakpoint was crossed. A grid must not
	 * show the clones, so it is torn down right away; the slider is rebuilt by the
	 * resize observer, which fires because the logo set changes its width.
	 *
	 * @return {void}
	 */
	handleModeChange() {

		if ( this.syncResponsiveMode() ) {

			this.stop();

		}

	}

	/**
	 * Work out whether this viewport should show the grid or the slider.
	 *
	 * @return {string} Either `grid` or `slider`.
	 */
	getCurrentMode() {

		if ( this.breakpointQuery.matches ) {

			return 'slider';

		}

		return this.element.classList.contains( 'b-logo-slider--layout-grid' ) ? 'grid' : 'slider';

	}

	/**
	 * Toggle the grid/slider classes for the current viewport. Cheap and independent of
	 * the logo images, so it can run before they have loaded.
	 *
	 * @return {boolean} True when the grid layout applies.
	 */
	syncResponsiveMode() {

		const isGrid = this.getCurrentMode() === 'grid';

		this.element.classList.toggle( 'b-logo-slider--responsive-grid', isGrid );
		this.element.classList.toggle( 'b-logo-slider--responsive-slider', ! isGrid );

		return isGrid;

	}

	/**
	 * Decide whether the logos should scroll and make sure the row holds enough copies
	 * for a gapless loop. Works on the sizes stored from the resize observer only.
	 *
	 * @return {void}
	 */
	update() {

		if ( this.getCurrentMode() === 'grid' ) {

			this.stop();

			return;

		}

		// A slider inside a hidden container (tab, accordion) has nothing to measure.
		// It will be updated again once it is shown and gets a real width.
		if ( ! this.containerWidth || ! this.itemWidth ) {

			return;

		}

		const overflows = this.itemWidth > this.containerWidth;
		const shouldAnimate = ! this.prefersReducedMotion && ( this.alwaysAnimate || overflows || this.isCrowded );

		if ( ! shouldAnimate ) {

			this.stop();

			return;

		}

		const period = this.itemWidth + this.gap;

		// Each half of the row has to cover the container on its own. Sizing it against
		// the screen instead of the current container means resizing the window never
		// needs more clones. The count only grows: spare clones off screen cost nothing,
		// while removing them would restart the animation.
		const target = Math.max( this.containerWidth, window.screen.width || 0 );
		const copies = Math.max( this.copies, Math.ceil( target / period ) );

		this.applyAnimationSpeed( copies * period );

		if ( copies !== this.copies ) {

			this.build( copies );

		}

	}

	/**
	 * Fill the row with two identical halves of `copies` logo sets each and restart the
	 * animation.
	 *
	 * @param {number} copies Logo sets per half of the row.
	 * @return {void}
	 */
	build( copies ) {

		this.removeClones();

		for ( let copy = 1; copy < copies * 2; copy++ ) {

			this.appendClone();

		}

		this.copies = copies;

		// Reading the width in between makes the browser treat the re-added class as a
		// fresh animation instead of coalescing both changes into a no-op.
		this.row.classList.remove( PLAY_CLASS );
		void this.row.offsetWidth;
		this.row.classList.add( PLAY_CLASS );

	}

	/**
	 * Stop the animation and drop the clones, leaving the logos from the markup.
	 *
	 * @return {void}
	 */
	stop() {

		if ( ! this.row ) {

			return;

		}

		window.clearTimeout( this.updateTimeout );

		this.row.classList.remove( PLAY_CLASS );
		this.removeClones();
		this.copies = 0;

	}

	/**
	 * Drop the clones added by a previous pass, leaving the item from the markup.
	 *
	 * @return {void}
	 */
	removeClones() {

		this.row.querySelectorAll( '.js-logo-slider-marquee-item.clone' ).forEach( ( clone ) => clone.remove() );

	}

	/**
	 * Append one copy of the logo set, hidden from assistive technology and from
	 * keyboard navigation. The original images have loaded by now, so the clones get
	 * them from memory instead of lazy loading them a second time.
	 *
	 * @return {void}
	 */
	appendClone() {

		const clone = this.item.cloneNode( true );

		clone.classList.add( 'clone' );
		clone.setAttribute( 'aria-hidden', 'true' );
		clone.querySelectorAll( 'img' ).forEach( ( img ) => {

			img.loading = 'eager';

		} );
		clone.querySelectorAll( 'a' ).forEach( ( link ) => {

			link.setAttribute( 'tabindex', '-1' );

		} );

		this.row.appendChild( clone );

	}

	/**
	 * Scale the configured animation speed to the distance of one lap, so a longer row
	 * takes proportionally longer instead of visibly speeding up.
	 *
	 * @param {number} distance Width of one half of the row, in px.
	 * @return {void}
	 */
	applyAnimationSpeed( distance ) {

		const duration = `${ ( ( this.animationSpeed * distance ) / SPEED_REFERENCE_WIDTH ).toFixed( 2 ) }s`;

		// Changing the duration of a running animation makes it jump, so leave it alone
		// unless the distance really changed.
		if ( duration === this.duration ) {

			return;

		}

		this.duration = duration;
		this.element.style.setProperty( '--animation-speed', duration );

	}

}

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', LogoSlider.init );

} else {

	LogoSlider.init();

}
